// AdminDepositLedger.tsx — Phase 11E admin's per-agent deposit-ledger panel.
//
// Routed at /agents/:id/deposit. Shows the current deposit balance,
// target, and ledger history. Admin can Credit (deposit_in) or
// Refund (deposit_refund_on_exit) via a typed-amount + note modal —
// no more browser prompts.

import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';

interface LedgerRow {
  id: number; kind: string; amount: number; balance_after: number | null;
  source_ref: string | null; note: string | null; created_at: string;
  actor_admin_username: string | null;
}
interface Data {
  agentId: number;
  deposit_balance: number;
  deposit_target: number;
  rows: LedgerRow[];
}

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function DepositLedgerTab() {
  const { id } = useParams<{ id: string }>();
  const agentId = Number(id);
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [modal, setModal] = useState<null | 'credit' | 'refund'>(null);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.get<Data>(`/admin/agents/${agentId}/deposit/ledger`);
      setData(r);
    } catch (e) { setErr((e as ApiError).message); }
  }, [agentId]);
  useEffect(() => { load(); }, [load]);

  const submit = async (kind: 'credit' | 'refund', amount: number, note: string) => {
    setActing(true); setErr(null);
    try {
      await adminApi.post(`/admin/agents/${agentId}/deposit/${kind}`, { amount, note });
      setToast(kind === 'credit' ? `Credited ${inr(amount)} to deposit.` : `Refunded ${inr(amount)} from deposit.`);
      setModal(null);
      await load();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setActing(false); }
  };

  const ratioPct = data && data.deposit_target > 0
    ? Math.min(100, Math.max(0, (data.deposit_balance / data.deposit_target) * 100))
    : 0;
  const ratioTone = ratioPct >= 100 ? 'emerald'
                  : ratioPct >= 50  ? 'amber'
                  : 'rose';

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {err && <Banner tone="error">{err}</Banner>}
        {toast && <Banner tone="success">{toast}</Banner>}

        {/* Balance card with progress bar */}
        {data && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      className="rounded-2xl p-5
                                 bg-white dark:bg-arena-700/40
                                 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
            <div className="flex items-start gap-3 flex-wrap">
              <span className="grid place-items-center w-10 h-10 rounded-xl
                               bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200
                               dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30 shrink-0">
                <Icon name="account_balance" size={22} filled />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 font-bold">
                  Current deposit balance
                </div>
                <div className="text-3xl font-display font-black tabular-nums text-neutral-900 dark:text-white mt-0.5">
                  {inr(data.deposit_balance)}
                </div>
                <div className="text-[12px] text-arena-600 dark:text-neon-200/70 mt-0.5">
                  Target: <b>{inr(data.deposit_target)}</b>
                  <span className="mx-2 opacity-40">·</span>
                  <span className="tabular-nums">{ratioPct.toFixed(1)}% of target</span>
                </div>
              </div>
              <div className="flex gap-2 ml-auto">
                <button onClick={() => setModal('credit')} disabled={acting}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                                   bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700
                                   text-white text-[12.5px] font-bold
                                   shadow-[0_2px_10px_rgba(16,185,129,0.35)]
                                   transition active:scale-95 disabled:opacity-50">
                  <Icon name="add" size={14} /> Credit
                </button>
                <button onClick={() => setModal('refund')} disabled={acting}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                                   bg-white text-rose-700 ring-1 ring-inset ring-rose-200/60 hover:bg-rose-50
                                   dark:bg-arena-700/40 dark:text-rose-300 dark:ring-rose-400/30 dark:hover:bg-rose-400/10
                                   text-[12.5px] font-bold
                                   transition active:scale-95 disabled:opacity-50">
                  <Icon name="undo" size={14} /> Refund
                </button>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4 h-2 rounded-full bg-arena-100/60 dark:bg-arena-700/50 overflow-hidden">
              <motion.div initial={{ width: 0 }} animate={{ width: `${ratioPct}%` }}
                          transition={{ type: 'spring', stiffness: 80, damping: 20 }}
                          className={`h-full rounded-full ${
                            ratioTone === 'emerald' ? 'bg-gradient-to-r from-emerald-400 to-teal-500'
                            : ratioTone === 'amber' ? 'bg-gradient-to-r from-amber-400 to-orange-500'
                            : 'bg-gradient-to-r from-rose-400 to-red-500'
                          }`} />
            </div>
          </motion.div>
        )}

        {/* Ledger table */}
        {data && data.rows.length === 0 && (
          <EmptyState icon="receipt_long" title="No deposit activity yet"
                      subtitle="Once you credit the deposit or process a refund, every entry will be listed here." />
        )}

        {data && data.rows.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 }}
                      className="overflow-x-auto rounded-2xl
                                 bg-white dark:bg-arena-700/40
                                 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
            <table className="w-full text-[12.5px]">
              <thead className="bg-arena-50/40 dark:bg-arena-800/40 text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 border-b border-arena-200/40 dark:border-arena-700/40">
                <tr>
                  <th className="text-left  py-2.5 px-3">When</th>
                  <th className="text-left  py-2.5 px-3">Kind</th>
                  <th className="text-right py-2.5 px-3">Amount</th>
                  <th className="text-right py-2.5 px-3">Balance after</th>
                  <th className="text-left  py-2.5 px-3">By</th>
                  <th className="text-left  py-2.5 px-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => {
                  const isIn = r.amount >= 0;
                  return (
                    <tr key={r.id}
                        className="border-b last:border-0 border-arena-200/40 dark:border-arena-700/40
                                   hover:bg-arena-50/60 dark:hover:bg-white/[0.03] transition">
                      <td className="py-2 px-3 text-arena-600 dark:text-neon-200/60 whitespace-nowrap text-[11.5px]">
                        {new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider ${
                          isIn
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                            : 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'
                        }`}>
                          <Icon name={isIn ? 'arrow_downward' : 'arrow_upward'} size={10} />
                          {r.kind.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className={`py-2 px-3 text-right tabular-nums font-bold ${isIn ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                        {isIn ? '+' : '−'}{inr(Math.abs(r.amount))}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-arena-700 dark:text-neon-200/80">{inr(r.balance_after || 0)}</td>
                      <td className="py-2 px-3 text-[11.5px] text-arena-700 dark:text-neon-200/80">
                        {r.actor_admin_username
                          ? <><Icon name="shield_person" size={11} className="inline-block align-middle mr-0.5" />admin @{r.actor_admin_username}</>
                          : <span className="text-arena-300 dark:text-neon-200/30">—</span>}
                      </td>
                      <td className="py-2 px-3 text-[11.5px] text-arena-600 dark:text-neon-200/60 max-w-xs truncate" title={r.note || ''}>
                        {r.note || <span className="text-arena-300 dark:text-neon-200/30">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>
        )}

        <div>
          <Link to={`/people/agents/${agentId}`}
                className="inline-flex items-center gap-1 text-[12px] font-semibold
                           text-arena-700 dark:text-neon-200/80 hover:text-neutral-900
                           dark:text-white/55 dark:hover:text-white">
            <Icon name="arrow_back" size={14} /> Back to agent detail
          </Link>
        </div>
      </div>

      {modal && data && (
        <AmountModal kind={modal} agentUsername={`#${agentId}`} busy={acting}
                     currentBalance={data.deposit_balance}
                     onCancel={() => setModal(null)}
                     onConfirm={(amt, note) => submit(modal, amt, note)} />
      )}
    </div>
  );
}

function AmountModal({
  kind, agentUsername, busy, currentBalance, onCancel, onConfirm,
}: {
  kind: 'credit' | 'refund'; agentUsername: string; busy: boolean; currentBalance: number;
  onCancel: () => void;
  onConfirm: (amount: number, note: string) => void;
}) {
  const [amt, setAmt] = useState('');
  const [note, setNote] = useState('');
  const amtNum = Math.floor(Number(amt));
  const valid = amtNum > 0 && (kind === 'credit' || amtNum <= currentBalance);
  const overRefund = kind === 'refund' && amtNum > 0 && amtNum > currentBalance;

  const tone = kind === 'credit' ? {
    accent: 'emerald', label: 'Credit deposit',
    blurb: `Add to agent ${agentUsername}'s deposit balance. Funds are tracked separately from the operating wallet.`,
    btn: 'bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700',
    shadow: 'shadow-[0_2px_10px_rgba(16,185,129,0.35)]',
    icon: 'add',
    badge: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30',
  } : {
    accent: 'rose', label: 'Refund deposit',
    blurb: `Remove from agent ${agentUsername}'s deposit balance. Use on exit / partial refund.`,
    btn: 'bg-rose-600 hover:bg-rose-500 active:bg-rose-700',
    shadow: 'shadow-[0_2px_10px_rgba(244,63,94,0.35)]',
    icon: 'undo',
    badge: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/30',
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-start sm:place-items-center p-4 overflow-y-auto" onClick={onCancel}>
      <motion.div onClick={(e) => e.stopPropagation()}
                  initial={{ opacity: 0, scale: 0.95, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                  className="rounded-2xl max-w-md w-full p-5 space-y-4
                             bg-white dark:bg-arena-800
                             ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                             shadow-2xl">
        <div className="flex items-start gap-3">
          <span className={`grid place-items-center w-10 h-10 rounded-xl shrink-0 ring-1 ${tone.badge}`}>
            <Icon name={tone.icon} size={20} filled />
          </span>
          <div>
            <h3 className="text-[15px] font-bold text-neutral-900 dark:text-white">{tone.label}</h3>
            <p className="text-[12px] text-arena-700 dark:text-white/60 mt-0.5">{tone.blurb}</p>
          </div>
        </div>

        <label className="block">
          <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">
            Amount (₹)
          </span>
          <input type="number" min="1" step="1" inputMode="numeric" value={amt} onChange={(e) => setAmt(e.target.value)}
                 placeholder="e.g. 1000"
                 autoFocus
                 className="w-full rounded-xl px-3 py-2 text-[14px] tabular-nums
                            bg-white dark:bg-arena-800/60
                            ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                            text-neutral-900 dark:text-white
                            placeholder-arena-400 dark:placeholder-neon-200/40
                            outline-none focus:outline-none focus:ring-2 focus:ring-violet-400/60" />
          {kind === 'refund' && (
            <div className="text-[10.5px] text-arena-600 dark:text-neon-200/55 mt-1 tabular-nums">
              Available: {inr(currentBalance)}
            </div>
          )}
          {overRefund && (
            <div className="text-[10.5px] text-rose-600 dark:text-rose-400 mt-1 font-semibold">
              Cannot refund more than current deposit balance.
            </div>
          )}
        </label>

        <label className="block">
          <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">
            Note (optional)
          </span>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder={kind === 'credit' ? 'e.g. Bank transfer received via UPI ref ABC123' : 'e.g. Partial refund on agent exit'}
                    className="w-full rounded-xl px-3 py-2 text-[13px]
                               bg-white dark:bg-arena-800/60
                               ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                               text-neutral-900 dark:text-white
                               placeholder-arena-400 dark:placeholder-neon-200/40
                               outline-none focus:outline-none focus:ring-2 focus:ring-violet-400/60 resize-y" />
        </label>

        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} disabled={busy}
                  className="px-4 py-2 rounded-full text-[12px] font-bold
                             bg-white text-arena-700 dark:text-neon-200/80 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50
                             dark:bg-arena-700/40 dark:text-neon-200/80 dark:ring-arena-600/40 dark:hover:bg-arena-700/60
                             disabled:opacity-50">
            Cancel
          </button>
          <button onClick={() => onConfirm(amtNum, note.trim())} disabled={!valid || busy}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                             ${tone.btn} text-white text-[12px] font-bold
                             ${tone.shadow}
                             transition active:scale-95 disabled:opacity-50`}>
            {busy ? <Icon name="progress_activity" size={13} className="animate-spin" /> : <Icon name={tone.icon} size={13} />}
            Confirm {kind}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success' | 'info'; children: React.ReactNode }) {
  const m = tone === 'error'
    ? 'bg-rose-50 dark:bg-rose-900/30 border-rose-200 dark:border-rose-800/50 text-rose-700 dark:text-rose-200'
    : tone === 'success' ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800/50 text-emerald-700 dark:text-emerald-200'
    : 'bg-arena-100/60 dark:bg-arena-800/40 ring-arena-200/60 dark:ring-arena-600/40 text-arena-800 dark:text-neon-200/90';
  return <div className={`rounded-2xl border px-4 py-3 text-[13px] ${m}`}>{children}</div>;
}

function EmptyState({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 px-6 py-12 text-center">
      <div className="mx-auto grid place-items-center w-14 h-14 rounded-2xl bg-arena-100/60 dark:bg-arena-700/50 mb-3">
        <Icon name={icon} size={26} className="text-arena-500 dark:text-neon-200/45" />
      </div>
      <div className="font-bold text-neutral-900 dark:text-white">{title}</div>
      <div className="text-[12px] text-arena-600 dark:text-neon-200/70 mt-1">{subtitle}</div>
    </div>
  );
}
